import React, { useState, useEffect } from 'react';
import { auth, db, storage } from './firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'; // Resumable သုံးရင် ပိုမြန်/ပိုသေချာတယ်
import { signOut } from 'firebase/auth';

const Profile = ({ onBack }) => {
    const [userData, setUserData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [newName, setNewName] = useState("");
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (auth.currentUser) {
            const userRef = doc(db, "users", auth.currentUser.uid);
            const unsubscribe = onSnapshot(userRef, (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setUserData(data);
                    setNewName(data.name || data.displayName || "");
                }
            });
            return () => unsubscribe();
        }
    }, []);

    const handleUpdateName = async () => {
        if (!newName.trim()) return;
        try {
            const userRef = doc(db, "users", auth.currentUser.uid);
            // Firestore မှာ name ရော displayName ရော တူအောင် update လုပ်ပေးလိုက်မယ်
            await updateDoc(userRef, { 
                name: newName,
                displayName: newName 
            });
            setIsEditing(false);
        } catch (err) { alert("Error updating name: " + err.message); }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // ဖိုင်ဆိုဒ် အရမ်းကြီးရင် (2MB ထက်ကြီးရင်) ကန့်သတ်ဖို့ လိုနိုင်တယ်
        if (file.size > 2 * 1024 * 1024) {
            alert("File is too large. Please upload less than 2MB.");
            return;
        }

        setUploading(true);
        // UID နဲ့ပဲ သိမ်းမှာဖြစ်လို့ အမြဲတမ်း တစ်ပုံပဲ ရှိနေမယ် (Storage မပြည့်အောင်)
        const storageRef = ref(storage, `profiles/${auth.currentUser.uid}`);
        
        try {
            // ၁။ ပုံကိုအရင်တင်မယ်
            const uploadTask = uploadBytesResumable(storageRef, file);
            
            uploadTask.on('state_changed', 
                null, 
                (error) => { throw error; }, 
                async () => {
                    // ၂။ တင်ပြီးရင် URL ယူမယ်
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    // ၃။ Firestore မှာ URL သိမ်းမယ်
                    const userRef = doc(db, "users", auth.currentUser.uid);
                    await updateDoc(userRef, { photoURL: url });
                    setUploading(false);
                }
            );
        } catch (err) { 
            alert("Upload failed: " + err.message); 
            setUploading(false);
        }
    };

    if (!userData) return <div style={{textAlign: 'center', padding: '50px', background: '#FDF7F0', minHeight: '100vh'}}>Loading...</div>;

    return (
        <div style={{ padding: '20px', background: '#FDF7F0', minHeight: '100vh', fontFamily: 'sans-serif' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                <span className="material-icons-round" onClick={onBack} style={{ cursor: 'pointer', fontSize: '24px', color: '#333' }}>arrow_back</span>
                <h2 style={{ marginLeft: '20px', fontSize: '20px', fontWeight: 'bold', color: '#333' }}>Edit Profile</h2>
            </div>

            {/* Profile Image Section */}
            <div style={{ textAlign: 'center', marginBottom: '40px', position: 'relative' }}>
                <div style={{ width: '120px', height: '120px', margin: '0 auto', borderRadius: '50%', border: '4px solid #fff', overflow: 'hidden', background: '#f9f9f9', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: '0 5px 15px rgba(0,0,0,0.1)' }}>
                    {userData.photoURL ? (
                        <img src={userData.photoURL} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <span className="material-icons-round" style={{ fontSize: '80px', color: '#ccc' }}>account_circle</span>
                    )}
                </div>
                
                <label style={{
                    position: 'absolute', bottom: '0', left: '58%', transform: 'translateX(-50%)',
                    background: '#E5B95F', color: '#fff', borderRadius: '50%', width: '38px', height: '38px',
                    display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', border: '3px solid #FDF7F0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                    <span className="material-icons-round" style={{ fontSize: '20px' }}>photo_camera</span>
                    <input type="file" onChange={handleImageUpload} hidden accept="image/*" />
                </label>
                {uploading && <p style={{ fontSize: '13px', color: '#E5B95F', fontWeight: 'bold', marginTop: '10px' }}>Upgrading Profile...</p>}
            </div>

            {/* Info Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                    <label style={{ fontSize: '13px', color: '#888', marginBottom: '8px', display: 'block' }}>User ID</label>
                    <div style={{ background: '#fff', padding: '15px', borderRadius: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #eee' }}>
                        <span style={{ fontWeight: 'bold', color: '#333' }}>#{auth.currentUser.uid.slice(0, 8).toUpperCase()}</span>
                        <span className="material-icons-round" style={{ color: '#ccc', fontSize: '20px', cursor: 'pointer' }}>content_copy</span>
                    </div>
                </div>

                <div>
                    <label style={{ fontSize: '13px', color: '#888', marginBottom: '8px', display: 'block' }}>Display Name</label>
                    <div style={{ position: 'relative' }}>
                        <input 
                            value={newName} 
                            onChange={(e) => setNewName(e.target.value)}
                            disabled={!isEditing}
                            style={{ 
                                width: '100%', padding: '15px', borderRadius: '15px', border: '1px solid #eee',
                                background: isEditing ? '#fff' : '#f8f9fa', boxSizing: 'border-box', fontSize: '15px',
                                outline: 'none', color: '#333'
                            }}
                        />
                        <span 
                            onClick={() => isEditing ? handleUpdateName() : setIsEditing(true)}
                            className="material-icons-round" 
                            style={{ position: 'absolute', right: '15px', top: '15px', color: '#E5B95F', cursor: 'pointer' }}
                        >
                            {isEditing ? "check_circle" : "edit"}
                        </span>
                    </div>
                </div>

                <div>
                    <label style={{ fontSize: '13px', color: '#888', marginBottom: '8px', display: 'block' }}>Email Address</label>
                    <div style={{ background: '#fff', padding: '15px', borderRadius: '15px', color: '#666', border: '1px solid #eee' }}>
                        {auth.currentUser.email}
                    </div>
                </div>

                <button 
                    onClick={() => signOut(auth)}
                    style={{
                        marginTop: '20px', width: '100%', padding: '16px', borderRadius: '15px', 
                        border: 'none', background: '#fff', color: '#ff4d4d', 
                        fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.05)', cursor: 'pointer'
                    }}
                >
                    <span className="material-icons-round">logout</span>
                    Logout
                </button>
            </div>
        </div>
    );
};

export default Profile;